import React, { useEffect, useState } from 'react';
import RunActivityPanel, { buildActivityCopyText } from 'features/agent-analytics/components/RunActivityPanel';
import RunFeedbackPanel, { feedbackFor } from 'features/agent-analytics/components/RunFeedbackPanel';
import TargetSignals from 'features/issues/components/TargetSignals';
import ConversationRunsPanel from 'features/agent-analytics/components/ConversationRunsPanel';
import Eval3VerdictsPanel, { useEval3Count } from 'features/evals3/components/Eval3VerdictsPanel';

function CopyGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function CheckGlyph({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

/** Activity-tab controls that live in the tab bar (right side): a Formatted/JSON
 *  mode toggle and a copy-icon button. Only shown while the Activity tab is open. */
function ActivityControls({ mode, onMode, onCopy, copied, span }) {
  return (
    <div className="ml-auto flex flex-none items-center gap-2 pr-1">
      <div className="inline-flex h-6 overflow-hidden rounded-md border border-[color:var(--border-default)] text-[11px]">
        <button
          type="button"
          onClick={() => onMode('formatted')}
          className={`px-2.5 transition-colors ${mode === 'formatted' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}
        >
          Formatted
        </button>
        <button
          type="button"
          onClick={() => onMode('json')}
          className={`border-l border-[color:var(--border-default)] px-2.5 transition-colors ${mode === 'json' ? 'bg-surface-hover text-[color:var(--text-primary)] font-medium' : 'bg-surface text-[color:var(--text-secondary)] hover:bg-surface-hover'}`}
        >
          JSON
        </button>
      </div>
      <button
        type="button"
        onClick={onCopy}
        title={span ? 'Copy all span details' : 'Copy all run details'}
        aria-label="Copy details"
        className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface text-[color:var(--text-secondary)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
      >
        {copied ? <CheckGlyph /> : <CopyGlyph />}
      </button>
    </div>
  );
}

function TabButton({ id, label, badge, active, onClick }) {
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={`relative px-3 py-2 text-[12px] font-medium transition-colors ${
        active ? 'text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]'
      }`}
    >
      <span className="inline-flex items-center gap-1.5">
        {label}
        {badge != null ? (
          <span className={`rounded-full px-1.5 text-[10px] font-semibold ${
            active ? 'bg-surface-hover text-[color:var(--text-primary)]' : 'bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)]'
          }`}>
            {badge}
          </span>
        ) : null}
      </span>
      <span
        className={`absolute inset-x-2 bottom-0 h-[2px] rounded-t ${active ? 'bg-[color:var(--text-primary)]' : 'bg-transparent'}`}
      />
    </button>
  );
}

export default function RunRightTabs({
  teamId,
  activeTab,
  onChangeTab,
  run,
  selectedSpan,
  onClearSpan,
  feedback,
  onSubmitFeedback,
  conversationRuns,
  activeRunId,
  onSelectRun,
  onRunClick,
  onSpanClick,
  onOpenContext,
  highlight = null,
  className = '',
}) {
  const convCount = conversationRuns?.length || 0;
  const [signalsCount, setSignalsCount] = useState(0);
  // Bumped when a human grade lands, so the Evaluations list re-fetches and the
  // score you just gave shows up next to the automated ones.
  // Kept as the refresh key the eval3 panel and its count share. Nothing
  // bumps it since the old evals' grading list came out of this tab; a
  // grade made in the new panel refreshes from inside it.
  const [gradeTick] = useState(0);
  // Activity view mode + copy live here so the controls can sit in the tab bar.
  const [activityMode, setActivityMode] = useState('formatted');
  // Landing from a search result forces the formatted view. The raw JSON view
  // renders payloads into a <pre> that never routes through HighlightText, so
  // a user who had left the toggle on JSON would click a result and arrive at
  // an unhighlighted wall of text — the exact failure the spotlight exists to
  // fix. Keyed on ts so it only fires on a fresh pick, leaving the toggle
  // under the user's control afterwards.
  useEffect(() => {
    if (highlight?.ts) setActivityMode('formatted');
  }, [highlight?.ts]);
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    const text = buildActivityCopyText(run, selectedSpan);
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1500); };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else { done(); }
  };
  // When a span is selected the whole right panel goes span-scoped: the same
  // main tabs show that span's activity / scores / signals, and Conversations
  // (a run-only concept) is hidden.
  const isSpan = !!selectedSpan;
  const spanId = selectedSpan?.span_id || null;

  // Scores merges user feedback + human evals + automated evals into one tab.
  // Map legacy ids onto it, and fall back off Conversations while span-scoped.
  // Comments moved into Activity, so anything that used to ask for the
  // Annotations tab — a Slack deep link, an inbox link, ?tab=annotations —
  // lands on Activity, where the comments now are, instead of a dead tab.
  let tab = (activeTab === 'feedback' || activeTab === 'evaluations') ? 'scores' : activeTab;
  if (tab === 'annotations') tab = 'activity';
  if (isSpan && tab === 'conversations') tab = 'activity';
  // Feedback on the run, or with a span selected, on that span (span_id).
  const v3Count = useEval3Count(teamId, isSpan ? 'span' : 'run', isSpan ? spanId : run?.run_id, gradeTick);
  const scoresBadge = (feedbackFor(feedback, spanId).length + v3Count) || null;

  useEffect(() => {
    setSignalsCount(0); // repopulated when the (span or run) Signals tab mounts
  }, [isSpan, spanId, run?.run_id]);

  return (
    <div className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-surface ${className}`}>
      <div className="flex items-center gap-1 border-b border-[color:var(--border-default)] bg-surface px-3 overflow-x-auto [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
        <TabButton id="activity"      label="Activity"      active={tab === 'activity'}      onClick={onChangeTab} />
        <TabButton id="scores"        label="Scores"        active={tab === 'scores'}        onClick={onChangeTab}
                   badge={scoresBadge} />
        {!isSpan ? (
          <TabButton id="conversations" label="Conversations" active={tab === 'conversations'} onClick={onChangeTab}
                     badge={convCount || null} />
        ) : null}
        <TabButton id="signals"       label="Signals"       active={tab === 'signals'}       onClick={onChangeTab}
                   badge={signalsCount || null} />
        {tab === 'activity' ? (
          <ActivityControls
            mode={activityMode}
            onMode={setActivityMode}
            onCopy={handleCopy}
            copied={copied}
            span={selectedSpan}
          />
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {tab === 'activity' ? (
          <RunActivityPanel
            teamId={teamId}
            run={run}
            selectedSpan={selectedSpan}
            onClearSpan={onClearSpan}
            onRunClick={onRunClick}
            onSpanClick={onSpanClick}
            highlight={highlight}
            mode={activityMode}
            onOpenContext={onOpenContext}
          />
        ) : tab === 'scores' ? (
          <div className="flex flex-col gap-5">
            {/* The old evals' Pending and Evaluations sections stood here
                until TRO-475. The engine and its data are untouched — this is
                the UI coming off, so a trace shows one set of scores and they
                are the new ones. */}
            <Eval3VerdictsPanel teamId={teamId} target={isSpan ? 'span' : 'run'} unitId={isSpan ? spanId : run?.run_id} refreshKey={gradeTick} />
            <RunFeedbackPanel feedback={feedback} spanId={spanId} onSubmit={onSubmitFeedback} />
          </div>
        ) : tab === 'signals' ? (
          <TargetSignals
            teamId={teamId}
            kind={isSpan ? 'span' : 'run'}
            id={isSpan ? spanId : run?.run_id}
            onCount={setSignalsCount}
            onSpanClick={onSpanClick}
          />
        ) : (
          <ConversationRunsPanel
            teamId={teamId}
            conversationId={run?.conversation_id}
            around={run?.started_at}
            activeRunId={activeRunId}
            onSelect={onSelectRun}
            fallbackRuns={conversationRuns || []}
          />
        )}
      </div>
    </div>
  );
}
